import { useAutoneTranslation } from '@autone/translations';
import { XIcon } from 'lucide-react';
import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';

import { Badge } from '../../../atoms';
import {
  TooltipContentCard,
  TooltipRoot,
  TooltipTrigger,
} from '../../../atoms/Tooltip';
import { RenderWithOverflow } from '../../../renderers';
import { type AutoneFilterChipBadge } from '../../core-general-filters';

export interface AutoneScopeDropdownBadgesProps {
  badgeList: AutoneFilterChipBadge[];
  onClearAll: () => void;
}

export type AutoneScopeDropdownBadgesRef = Map<string, HTMLElement | null>;

export const AutoneScopeDropdownBadges = forwardRef<
  AutoneScopeDropdownBadgesRef,
  AutoneScopeDropdownBadgesProps
>(({ badgeList, onClearAll }, ref) => {
  const { common } = useAutoneTranslation();
  const badgeRefs = useRef<AutoneScopeDropdownBadgesRef>(new Map());

  useImperativeHandle(ref, () => badgeRefs.current);

  const setBadgeRef = useCallback(
    (key: string) => (el: HTMLElement | null) => {
      if (el) {
        badgeRefs.current.set(key, el);
      } else {
        badgeRefs.current.delete(key);
      }
    },
    [],
  );

  if (badgeList.length <= 2) {
    return badgeList.map((badge) => (
      <Badge key={badge.key} ref={setBadgeRef(badge.key)} variant="secondary">
        <div className="inline-flex gap-1.5 items-center">
          <span className="block max-w-[15ch] truncate">{badge.text}</span>
          <button
            className="transition-colors aspect-square rounded-sm h-fit p-0.5"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              badge.handleDelete();
            }}
          >
            <XIcon size={12} />
          </button>
        </div>
      </Badge>
    ));
  }

  return (
    <TooltipRoot delayDuration={200}>
      <TooltipTrigger asChild>
        <Badge
          variant="secondary"
          className="shadow-sm"
          ref={setBadgeRef('clear-all')}
        >
          <div className="inline-flex gap-1.5 items-center">
            {`${badgeList.length} ${common('selected')}`}
            <button
              className="transition-colors aspect-square rounded-sm h-fit p-0.5"
              type="button"
              onClick={onClearAll}
            >
              <XIcon size={12} />
            </button>
          </div>
        </Badge>
      </TooltipTrigger>
      <TooltipContentCard
        data-testid="autone-scope-dropdown-badges-tooltip-content"
        sideOffset={6}
        className="max-w-[300px] p-2"
        onClick={(e) => e.stopPropagation()}
        onFocus={(e) => e.stopPropagation()}
      >
        <div className="flex gap-1 items-center flex-wrap">
          <RenderWithOverflow
            items={badgeList}
            maxVisible={20}
            renderItem={(item) => (
              <Badge
                key={item.key}
                ref={setBadgeRef(item.key)}
                variant="secondary"
              >
                <div className="inline-flex gap-1.5 items-center">
                  {item.text}
                  <button
                    className="transition-colors hover:bg-muted aspect-square rounded-sm h-fit p-0.5"
                    type="button"
                    onClick={item.handleDelete}
                  >
                    <XIcon size={12} />
                  </button>
                </div>
              </Badge>
            )}
            renderOverflow={(overflowItems) => (
              <Badge variant="secondary" className="cursor-default">
                +{overflowItems.length}
              </Badge>
            )}
          />
        </div>
      </TooltipContentCard>
    </TooltipRoot>
  );
});

AutoneScopeDropdownBadges.displayName = 'AutoneScopeDropdownBadges';
